import { ref, getCurrentInstance, onUnmounted } from 'vue';
import { useAppStore } from '@/stores/app';
import { getSessionState } from '@/services/api';

/**
 * Session-expiry monitor for the frozen wizard (spec 008-freeze-steps-tabs).
 *
 * Improvements over the original 15s `setInterval` poll in App.vue:
 *  - Event-driven: a single `setTimeout` armed to `sessionExpiresAt - now`
 *    (plus a second timer for the pre-expiry warning) — no busy polling, and
 *    the reset fires the instant the session lapses instead of up to 15s late.
 *  - Pre-expiry warning: `isExpiringSoon` flips true `warnLeadMs` before expiry
 *    so the UI can surface a "session expiring soon" banner.
 *  - Server-confirmed unfreeze: when the local clock says the session has
 *    lapsed, we first ask the backend for the authoritative `expires_at`. If
 *    the server still considers the session valid (client clock skew), we
 *    re-sync the expiry and re-arm instead of wrongly resetting a live session.
 *  - Re-arm on demand: callers invoke start() after re-auth/resume, and the
 *    server-confirm path re-schedules itself after a clock-skew re-sync.
 */

// setTimeout overflows past its 32-bit signed max and would fire immediately;
// clamp long waits and let the handler re-arm. Sessions are ~30min so this is
// just a safety net.
const MAX_TIMEOUT_MS = 2_147_483_647;
const DEFAULT_WARN_LEAD_MS = 60_000;

export interface UseSessionExpiryOptions {
  /** How long before expiry to flip `isExpiringSoon` (ms). Default 60_000. */
  warnLeadMs?: number;
}

export function useSessionExpiry(options: UseSessionExpiryOptions = {}) {
  const store = useAppStore();
  const warnLeadMs = options.warnLeadMs ?? DEFAULT_WARN_LEAD_MS;

  const expiryTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  const isExpiringSoon = ref(false);

  function clearTimers() {
    if (expiryTimer.value) {
      clearTimeout(expiryTimer.value);
      expiryTimer.value = null;
    }
    if (warnTimer.value) {
      clearTimeout(warnTimer.value);
      warnTimer.value = null;
    }
  }

  /** Stop monitoring and clear any warning state (no app reset). */
  function stop() {
    clearTimers();
    isExpiringSoon.value = false;
  }

  /** Hide the warning banner without stopping the monitor. */
  function dismissWarning() {
    isExpiringSoon.value = false;
  }

  /** Clear the expired session and return the wizard to an interactive landing. */
  function reset() {
    stop();
    store.clearExpiredSessionState();
    store.setStep('landing');
  }

  /** Local-clock says it lapsed — confirm with the server before resetting. */
  async function handleExpiry() {
    const sid = store.sessionId;
    if (!sid) {
      stop();
      return;
    }
    try {
      const state = await getSessionState(sid);
      const serverExpiry = state.expires_at ? new Date(state.expires_at).getTime() : 0;
      if (serverExpiry > Date.now()) {
        // Server still considers the session live (client clock skew) — re-sync
        // and re-arm rather than resetting a valid session.
        store.setSessionExpiresAt(state.expires_at!);
        schedule();
        return;
      }
    } catch {
      // Session gone / network error → fall through to the reset.
    }
    reset();
  }

  /** (Re)arm the warning + expiry timers from the current `sessionExpiresAt`. */
  function schedule() {
    clearTimers();
    isExpiringSoon.value = false;
    const expiresAt = store.sessionExpiresAt;
    if (!expiresAt) return;

    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      handleExpiry();
      return;
    }

    const warnMs = ms - warnLeadMs;
    if (warnMs <= 0) {
      isExpiringSoon.value = true;
    } else {
      warnTimer.value = setTimeout(() => {
        isExpiringSoon.value = true;
      }, Math.min(warnMs, MAX_TIMEOUT_MS));
    }

    expiryTimer.value = setTimeout(() => {
      handleExpiry();
    }, Math.min(ms, MAX_TIMEOUT_MS));
  }

  /** Begin monitoring (idempotent — safe to call repeatedly). */
  function start() {
    schedule();
  }

  // Lifecycle cleanup only when used inside a component; callable bare in tests.
  if (getCurrentInstance()) {
    onUnmounted(stop);
  }

  return { start, stop, schedule, dismissWarning, isExpiringSoon };
}
